import React from 'react';

// PharmaAnalytics.jsx
// Self-contained dashboard component showing key pharmacy metrics with Tailwind CSS styling.
// Replace the placeholder arrays/numbers with real data from APIs or props when integrating.

const PharmaAnalytics = () => {
  // ===== Placeholder data (replace with real data) =====
  const inventoryData = [
    { label: 'Antibiotics', value: 68 },
    { label: 'Analgesics', value: 52 },
    { label: 'Antipyretics', value: 34 },
    { label: 'Vitamins', value: 87 },
    { label: 'Antacids', value: 44 },
  ];

  const fulfillmentRate = 86; // % of prescriptions fulfilled (e.g., last 30 days)

  const revenueExpensesData = [
    { month: 'Apr', revenue: 420, expenses: 290 },
    { month: 'May', revenue: 460, expenses: 310 },
    { month: 'Jun', revenue: 510, expenses: 330 },
    { month: 'Jul', revenue: 480, expenses: 320 },
    { month: 'Aug', revenue: 560, expenses: 350 },
    { month: 'Sep', revenue: 590, expenses: 370 },
  ];

  const topSelling = [
    { name: 'Paracetamol 500mg', units: 1240, revenue: 2.48 },
    { name: 'Amoxicillin 250mg', units: 860, revenue: 3.01 },
    { name: 'Cetirizine 10mg', units: 740, revenue: 1.11 },
    { name: 'Omeprazole 20mg', units: 655, revenue: 1.64 },
    { name: 'Ibuprofen 400mg', units: 610, revenue: 1.83 },
  ];

  const patientVisits = [
    { month: 'Apr', visits: 820 },
    { month: 'May', visits: 910 },
    { month: 'Jun', visits: 980 },
    { month: 'Jul', visits: 940 },
    { month: 'Aug', visits: 1020 },
    { month: 'Sep', visits: 1085 },
  ];

  // ===== Small chart helpers (CSS/SVG only, no external libs) =====
  const MiniBarChart = ({ data = [], max = 100, height = 140, color = '#3b82f6' }) => {
    const computedMax = Math.max(max, ...data.map(d => d.value));
    return (
      <div className="h-40 flex items-end gap-2">
        {data.map((d, i) => {
          const h = Math.max((d.value / computedMax) * (height - 20), 8);
          return (
            <div key={i} className="flex flex-col items-center flex-1">
              <div className="text-[10px] text-gray-600 mb-1">{d.value}</div>
              <div className="w-full rounded-t" style={{ height: h, background: color }} />
              <div className="text-[10px] text-gray-500 mt-1 truncate w-full text-center">{d.label}</div>
            </div>
          );
        })}
      </div>
    );
  };

  const ProgressBar = ({ percent, track = 'bg-blue-50', fill = 'bg-emerald-500' }) => (
    <div className={`w-full h-3 ${track} rounded-full overflow-hidden`}>
      <div
        className={`${fill} h-full transition-all duration-500`}
        style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
      />
    </div>
  );

  const AreaLines = ({ seriesA = [], seriesB = [], width = 480, height = 160, pad = 20 }) => {
    const n = Math.max(seriesA.length, seriesB.length);
    const maxY = Math.max(
      1,
      ...seriesA.map(p => p.value ?? p),
      ...seriesB.map(p => p.value ?? p)
    );
    const x = i => pad + (i * (width - pad * 2)) / Math.max(1, n - 1);
    const y = v => height - pad - ((v / maxY) * (height - pad * 2));

    const path = arr =>
      arr.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)},${y(p.value ?? p)}`).join(' ');
    const area = arr =>
      `${path(arr)} L ${x(arr.length - 1)},${height - pad} L ${x(0)},${height - pad} Z`;

    const aPath = path(seriesA);
    const aArea = area(seriesA);
    const bPath = path(seriesB);
    const bArea = area(seriesB);

    return (
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-40">
        <defs>
          <linearGradient id="gradA" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.05" />
          </linearGradient>
          <linearGradient id="gradB" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#10b981" stopOpacity="0.05" />
          </linearGradient>
        </defs>
        <path d={aArea} fill="url(#gradA)" />
        <path d={bArea} fill="url(#gradB)" />
        <path d={aPath} fill="none" stroke="#3b82f6" strokeWidth="2" />
        <path d={bPath} fill="none" stroke="#10b981" strokeWidth="2" />
      </svg>
    );
  };

  const LineChart = ({ series = [], width = 480, height = 160, pad = 20, stroke = '#0ea5e9' }) => {
    const n = series.length;
    const maxY = Math.max(1, ...series.map(p => p.value ?? p));
    const x = i => pad + (i * (width - pad * 2)) / Math.max(1, n - 1);
    const y = v => height - pad - ((v / maxY) * (height - pad * 2));
    const d = series
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)},${y(p.value ?? p)}`)
      .join(' ');
    return (
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-40">
        <path d={d} fill="none" stroke={stroke} strokeWidth="2" />
      </svg>
    );
  };

  const Card = ({ title, icon, children, subtitle }) => (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm h-full">
      <div className="px-3 py-3 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h3 className="text-gray-800 font-semibold">{title}</h3>
          {subtitle && <p className="text-gray-500 text-sm mt-0.5">{subtitle}</p>}
        </div>
        <div className="text-2xl">{icon}</div>
      </div>
      <div className="p-3">{children}</div>
    </div>
  );

  const MedicineConsumptionChart = () => {
  // ===== Sample monthly consumption data (replace with API/props) =====
  const consumptionData = [
    { month: 'Jan', units: 320 },
    { month: 'Feb', units: 410 },
    { month: 'Mar', units: 500 },
    { month: 'Apr', units: 560 },
    { month: 'May', units: 640 },
    { month: 'Jun', units: 710 },
    { month: 'Jul', units: 790 },
    { month: 'Aug', units: 860 },
    { month: 'Sep', units: 940 },
    { month: 'Oct', units: 1020 },
    { month: 'Nov', units: 1110 },
    { month: 'Dec', units: 1200 },
  ];

  const width = 600;
  const height = 300;
  const pad = 40;

  // ==== Scaling functions ====
  const maxY = Math.max(...consumptionData.map(d => d.units));
  const x = i => pad + (i * (width - pad * 2)) / (consumptionData.length - 1);
  const y = v => height - pad - (v / maxY) * (height - pad * 2);

  // Line path for consumption
  const pathD = consumptionData
    .map((d, i) => `${i === 0 ? 'M' : 'L'} ${x(i)},${y(d.units)}`)
    .join(' ');

  // Optional: gradient fill below line for area effect
  const areaD =
    `${pathD} L ${x(consumptionData.length - 1)},${height - pad}` +
    ` L ${x(0)},${height - pad} Z`;

  return (
    <div className="bg-white w-full">
     
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-72 text-sky-600"
      >
        <defs>
          <linearGradient id="consumptionFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* X and Y axes */}
        <line
          x1={pad}
          y1={height - pad}
          x2={width - pad}
          y2={height - pad}
          stroke="#d1d5db"
        />
        <line
          x1={pad}
          y1={pad}
          x2={pad}
          y2={height - pad}
          stroke="#d1d5db"
        />

        {/* Horizontal grid lines */}
        {Array.from({ length: 5 }).map((_, i) => {
          const yPos = pad + ((height - pad * 2) * i) / 4;
          return (
            <line
              key={i}
              x1={pad}
              x2={width - pad}
              y1={yPos}
              y2={yPos}
              stroke="#f3f4f6"
            />
          );
        })}

        {/* Area under line */}
        <path d={areaD} fill="url(#consumptionFill)" />

        {/* Consumption line */}
        <path d={pathD} fill="none" stroke="#0ea5e9" strokeWidth="3" />

        {/* Data points */}
        {consumptionData.map((d, i) => (
          <circle
            key={i}
            cx={x(i)}
            cy={y(d.units)}
            r={4}
            fill="#0ea5e9"
            stroke="#fff"
            strokeWidth="1.5"
          />
        ))}

        {/* X-axis labels */}
        {consumptionData.map((d, i) => (
          <text
            key={i}
            x={x(i)}
            y={height - pad + 20}
            textAnchor="middle"
            className="text-[10px] fill-gray-600"
          >
            {d.month}
          </text>
        ))}

        {/* Y-axis labels */}
        {Array.from({ length: 5 }).map((_, i) => {
          const value = Math.round(maxY - (maxY * i) / 4);
          return (
            <text
              key={i}
              x={pad - 10}
              y={pad + ((height - pad * 2) * i) / 4 + 4}
              textAnchor="end"
              className="text-[10px] fill-gray-600"
            >
              {value}
            </text>
          );
        })}
      </svg>
      <p className="mt-4 text-sm text-gray-500">
        Showing total units of medicines consumed each month.
      </p>
    </div>
  );
};

 function StockStatusPie() {
  return (
    <div className="bg-white p-3">

      {/* Pie Chart */}
      <div
        className="
          relative w-44 h-44 mx-auto mb-4 rounded-full
          [background:conic-gradient(#10b981_0%_70%,#f59e0b_70%_90%,#ef4444_90%_100%)]
          flex items-center justify-center
        "
      >
        <span className="absolute text-sm font-semibold text-gray-700">
          Inventory
        </span>
      </div>

      {/* Legend */}
      <ul className="space-y-2 text-left text-sm mx-auto w-max">
        <li className="flex items-center">
          <span className="w-3 h-3 rounded-full bg-emerald-500 mr-2"></span>
          In-Stock – 70%
        </li>
        <li className="flex items-center">
          <span className="w-3 h-3 rounded-full bg-amber-500 mr-2"></span>
          Low-Stock – 20%
        </li>
        <li className="flex items-center">
          <span className="w-3 h-3 rounded-full bg-red-500 mr-2"></span>
          Out-of-Stock – 10%
        </li>
      </ul>
    </div>
  );
};

function StockStatusTable() {
  const data = [
    { category: "Antibiotics", inStock: 120, lowStock: 30, outOfStock: 5 },
    { category: "Painkillers", inStock: 90,  lowStock: 20, outOfStock: 10 },
    { category: "Vitamins",   inStock: 150, lowStock: 15, outOfStock: 2 },
    { category: "Supplements", inStock: 90,  lowStock: 25, outOfStock: 8 },
    { category: "Others",     inStock: 60,  lowStock: 25, outOfStock: 8 },
  ];

  return (
    <div className="bg-white">

      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="bg-gray-100 text-gray-700 text-sm uppercase tracking-wider">
              <th className="px-4 py-3 text-left">Category</th>
              <th className="px-4 py-3 text-center">In Stock</th>
              <th className="px-4 py-3 text-center">Low Stock</th>
              <th className="px-4 py-3 text-center">Out of Stock</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr
                key={i}
                className="border-b last:border-0 hover:bg-gray-50 transition-colors"
              >
                <td className="px-4 py-3 text-left font-medium text-gray-800">
                  {row.category}
                </td>
                <td className="px-4 py-3 text-center text-emerald-600 font-semibold">
                  {row.inStock}
                </td>
                <td className="px-4 py-3 text-center text-amber-600 font-semibold">
                  {row.lowStock}
                </td>
                <td className="px-4 py-3 text-center text-red-600 font-semibold">
                  {row.outOfStock}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

  return (
    <div className="w-full">
      {/* Header - replace with your page header UI */}
      <div className="p-6">
        <h1 className="text-2xl font-bold">Pharmacy Analytics</h1>
        <p className="text-white/80">Key operational metrics at a glance</p>
      </div>

      {/* Grid layout for metric cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {/* Inventory Levels */}
        <Card title="Inventory Levels" icon="📦" subtitle="Current stock by category">
          <MiniBarChart data={inventoryData} color="#0284c7" />
          <div className="mt-4 flex flex-wrap gap-3 text-xs">
            {inventoryData.map((d, i) => (
              <span
                key={i}
                className="px-2 py-1 bg-sky-50 text-sky-700 rounded-full border border-sky-200"
              >
                {d.label}: {d.value}
              </span>
            ))}
          </div>
        </Card>

        {/* Prescription Fulfillment Rate */}
        <Card title="Prescription Fulfillment" icon="✅" subtitle="Last 30 days">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-700 font-medium">Fulfillment Rate</span>
            <span className="text-emerald-600 font-semibold">{fulfillmentRate}%</span>
          </div>
          <ProgressBar percent={fulfillmentRate} />
          <StockStatusPie />
        </Card>

        {/* Revenue & Expenses */}
        <Card title="Revenue & Expenses" icon="💰" subtitle="Last 6 months">
          <AreaLines
            seriesA={revenueExpensesData.map(d => ({ value: d.revenue }))}
            seriesB={revenueExpensesData.map(d => ({ value: d.expenses }))}
          />
          <div className="flex items-center gap-4 text-sm mt-2">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-blue-500 rounded-full" />
              Revenue
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-emerald-500 rounded-full" />
              Expenses
            </div>
            <div className="ml-auto text-gray-500">
              Most recent: {revenueExpensesData[revenueExpensesData.length - 1].month}
            </div>
          </div>
        </Card>

        {/* Monthly Patient Visits */}
        <Card title="Monthly Medicine Consumption" icon="👥" subtitle="Clinic inflow">
          <MedicineConsumptionChart />
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>Min: {Math.min(...patientVisits.map(d => d.visits))}</span>
            <span>Max: {Math.max(...patientVisits.map(d => d.visits))}</span>
            <span>Months: {patientVisits.length}</span>
          </div>
        </Card>

        {/* Top-selling Medicines (Wide) */}
        <div className="col-span-2">
          <Card title="Top-selling Medicines" icon="💊" subtitle="Units and revenue (₹ lakhs)">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-600">
                    <th className="text-left font-medium px-4 py-3">Medicine</th>
                    <th className="text-right font-medium px-4 py-3">Units</th>
                    <th className="text-right font-medium px-4 py-3">Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {topSelling.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-800">{row.name}</td>
                      <td className="px-4 py-3 text-right">{row.units.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right">₹ {row.revenue.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
        <div className="col-span-2">
            <Card title="Detailed Stock Levels" icon="💊" subtitle="Units and revenue (₹ lakhs)">
                    <StockStatusTable />
            </Card>
            
        </div>
      </div>
    </div>
  );
};

export default PharmaAnalytics;
